import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Productor } from './visitas.service';

/**
 * Interfaz para un item de productor en la lista (según la respuesta paginada)
 */
export interface ProductorListItem {
  id_productor: string;
  nombre: string;
  primer_apellido?: string;
  segundo_apellido?: string;
  unidad_operativa: string | null;
  comuna: string | null;
  email: string | null;
  ultima_visita: string | null;
  coordenadas: {
    lat: number;
    lng: number;
  } | null;
}

/**
 * Interfaz para la respuesta paginada de productores
 */
export interface ProductorPaginationResponse {
  items: ProductorListItem[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

/** Respuesta paginada ya mapeada a `Productor` (para sync / drain-all). */
export interface ProductoresPaginados {
  items: Productor[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

/**
 * Servicio para gestionar productores desde la API
 * 
 * NOTA: El token de autenticación se agrega automáticamente a todas las peticiones
 * mediante el AuthInterceptor. No es necesario agregar headers manualmente.
 */
@Injectable({
  providedIn: 'root'
})
export class ProductorService {
  private apiUrl = environment.apiUrl;
  private readonly http = inject(HttpClient);

  private mapListItem(item: ProductorListItem): Productor {
    return {
      id_productor: item.id_productor,
      nombre: item.nombre,
      primer_apellido: item.primer_apellido,
      segundo_apellido: item.segundo_apellido,
      comuna: item.comuna,
      email: item.email,
      ultima_visita: item.ultima_visita,
      coordenadas: item.coordenadas,
      jerarquia: {
        descripcion: item.unidad_operativa || 'Sin unidad operativa'
      }
    };
  }

  /**
   * Listado paginado con metadatos `total`/`pages` (para sincronización offline).
   * Endpoint: GET /api/v1/productor/perfil/{id_perfil}/jerarquia/{id_jerarquia}
   */
  obtenerProductoresPaginados(
    id_perfil: number,
    id_jerarquia: number,
    search?: string,
    page: number = 1,
    size: number = 100
  ): Observable<ProductoresPaginados> {
    let httpParams = new HttpParams();
    httpParams = httpParams.set('page', page.toString());
    httpParams = httpParams.set('size', size.toString());
    if (search && search.trim()) {
      httpParams = httpParams.set('search', search.trim());
    }

    const url = `${this.apiUrl}/productor/perfil/${id_perfil}/jerarquia/${id_jerarquia}`;
    return this.http.get<ProductorPaginationResponse>(url, { params: httpParams }).pipe(
      map(response => ({
        items: (response.items || []).map(item => this.mapListItem(item)),
        total: response.total ?? 0,
        page: response.page ?? page,
        size: response.size ?? size,
        pages: Math.max(1, response.pages || 1),
      })),
      catchError(error => {
        console.error('Error al obtener productores:', error);
        return of({ items: [], total: 0, page, size, pages: 1 });
      })
    );
  }

  /**
   * Obtiene el listado de productores con búsqueda opcional.
   * Endpoint: GET /api/v1/productor/perfil/{id_perfil}/jerarquia/{id_jerarquia}
   * @param id_perfil ID del perfil (selector en header) — obligatorio
   * @param id_jerarquia ID de la jerarquía / unidad operativa (selector en header); -1 = Todas
   * @param search Término de búsqueda para filtrar productores
   * @param page Número de página (opcional, por defecto 1)
   * @param size Tamaño de página (opcional, por defecto 100 para obtener la mayoría)
   * @returns Observable con el array de productores
   */
  obtenerProductores(
    id_perfil: number,
    id_jerarquia: number,
    search?: string,
    page: number = 1,
    size: number = 100
  ): Observable<Productor[]> {
    return this.obtenerProductoresPaginados(id_perfil, id_jerarquia, search, page, size).pipe(
      map(resp => resp.items)
    );
  }

  /**
   * Obtiene un productor por su ID.
   * Endpoint: GET /api/v1/productor/perfil/{id_perfil}/jerarquia/{id_jerarquia}/{id_productor}
   * @param id_perfil ID del perfil — obligatorio
   * @param id_jerarquia ID de la jerarquía / unidad operativa; -1 = Todas
   * @param idProductor ID del productor
   * @returns Observable con el productor
   */
  obtenerProductorPorId(id_perfil: number, id_jerarquia: number, idProductor: string): Observable<Productor> {
    const url = `${this.apiUrl}/productor/perfil/${id_perfil}/jerarquia/${id_jerarquia}/${idProductor}`;
    return this.http.get<Productor>(url).pipe(
      catchError(error => {
        console.error('Error al obtener productor:', error);
        throw error;
      })
    );
  }
}
